import type { FC } from 'react'
import { useCallback, useMemo, useReducer, useRef, useState } from 'react'

import {
  type Batch,
  type BatchItem,
  type UploadInfo,
  FILE_STATES,
} from '@rpldy/shared'
import {
  useBatchAddListener,
  useBatchFinishListener,
  useBatchProgressListener,
  useItemErrorListener,
  useItemFinishListener,
  useItemProgressListener,
  useItemStartListener,
  useUploady,
  useFileInput,
} from '@rpldy/uploady'

import { BaseUpload, type IBaseUploadProps } from './BaseUpload'
import { FileActionContainer } from './components/FileActionContainer'
import { FileList } from './components/FileList'
import { FileListContainer } from './components/FileListContainer'
import { MainContainer } from './components/MainContainer'
import { UploadButton } from './components/UploadButton'
import { UploadContainer } from './components/UploadContainer'
import { UploadDragDropWrapper } from './components/UploadDragDropWrapper'
import { UploadEmptyState } from './components/UploadEmptyState'
import { UploadFile as UploadFileComponent } from './components/UploadFile'
import { UploadFileContainer } from './components/UploadFileContainer'
import { UploadProgress } from './components/UploadProgress'
import { UploadText } from './components/UploadText'
import {
  type UploadAnalyticsIds,
  type UploadFile,
  UploadFileStatuses,
} from './types'
import { UploadContext } from './UploadContext'
import { getImagePreview } from './utils'
import { UploaderStyled } from '../Uploader/styles/UploaderStyled'
import { TransparentIconButton } from '@ui/elements/Button'
import { Space } from '@ui/elements/Space'
import { SubText } from '@ui/elements/Text'
import { WithTooltip } from '@ui/elements/Tooltip'
import { filesize } from '@ui/utils/filters'

const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 * 10 // 10MB

export const UploadComponents = {
  MainContainer,
  FileActionContainer,
  FileListContainer,
  FileList,
  UploadFile: UploadFileComponent,
  UploadFileContainer,
  UploadContainer,
  UploadButton,
  UploadText,
  UploadEmptyState,
  UploadProgress,
  UploadDragDropWrapper,
}

// Builds the UploadComponents type from the actual components object, and
// updates the return type to allow null (for conditional rendering)
export type UploadComponents = {
  [K in keyof typeof UploadComponents]: (
    ...args: Parameters<(typeof UploadComponents)[K]>
  ) => ReturnType<(typeof UploadComponents)[K]> | null
}

export interface InitialUploadFile extends Partial<
  Omit<UploadFile, 'id' | 'file' | 'completed'>
> {
  id: UploadFile['file']
  file: UploadFile['file']
}

export interface IUploadProps extends IBaseUploadProps {
  /** Sets the [id] of the uploader */
  id?: string
  /**
   * Sets the analytics ids for components inside the uploader.
   * @note When overriding any decorated sub-component, be sure to forward the
   * relevant analyticsIds value to the wrapped element.
   */
  analyticsIds?: UploadAnalyticsIds
  initialFiles?: InitialUploadFile[]
  maxFileSize?: number
  readonly?: boolean
  /** When true, disables all interactions */
  disabled?: boolean
  /** When true, only disable upload functionality  */
  uploadDisabled?: boolean
  /** Determines whether to clear the file list before uploading a new batch of files */
  replaceFilelist?: boolean
  components?: Partial<UploadComponents>
  /**
   * Function used to get the url of the uploaded file from the server response
   */
  getFileUrl?(res: MigrateToStrictType): string
  /**
   * Function used to get the id of the uploaded file from the server response, stores in the `externalId` field
   */
  getExternalId?(res: MigrateToStrictType): string
  /**
   * Function used to get preview url of the file
   *
   * @note this function runs on all file types
   */
  getPreviewFile?(file: BatchItem['file'] | File): string | Promise<string>
  /**
   * Function called before a file is added to the upload queue
   *
   * return `false` or `Promise.resolve(false)` to prevent file from being added
   */
  onFileAdd?(file: UploadFile): void | false | Promise<void | false>
  /**
   * Function called when group of files are added to the upload queue
   */
  onBatchAdd?(
    files: UploadFile[],
    filelist: UploadFile[]
  ): void | false | Promise<void | false>
  /**
   * Function called when the same group of files added to the upload is completed
   */
  onBatchComplete?(files: UploadFile[], filelist: UploadFile[]): void
  /**
   * Function called when file(s) have its states changed
   */
  onChange?(files: UploadFile[], filelist: UploadFile[]): void
  /**
   * Function called when an individual upload item fails. Receives the
   * file with `status: ERROR` and the server response in `rawResponse`.
   */
  onItemError?(file: UploadFile): void
  /**
   * Function called before a file is deleted
   *
   * return `false` or `Promise.resolve(false)` to prevent file from being deleted
   */
  onDelete?(file: UploadFile): void | Promise<void | false> | false
  strings?: Partial<Record<keyof typeof defaultStrings, string>> &
    Record<string, string>
}

export interface UploadCommonProps<
  ExtraUploadProps extends Record<string, any> = Record<string, any>,
> {
  files: UploadFile[]
  batchProgress: Batch['completed']
  isMultiple: IUploadProps['multiple']
  isReadonly: IUploadProps['readonly']
  isDisabled: IUploadProps['disabled']
  isUploading: boolean
  isUploadDisabled: IUploadProps['uploadDisabled']
  acceptFiles: IUploadProps['allowedFileTypes']
  showFileUpload: () => void
  uploadFile: (filesToUpload: UploadInfo | UploadInfo[]) => void
  deleteFile: (fileOrFileID: string | UploadFile) => void
  cancelFile: (fileOrFileID: string | UploadFile) => void
  reset: () => void
  uploadProps: IUploadProps & ExtraUploadProps
  strings: Record<string, string>
}

const defaultStrings = {
  uploadInfo: 'or drag and drop files here',
  uploadButtonText: 'Upload',
  emptyStateInfo: 'No files attached',
  exceedsSizeLimit: 'Exceeds size limit',
  unacceptedFileType: 'Unaccepted file type',
  dragDropOverlayInfo: 'Drop files here to upload',
}

export const defaultUploadFile: UploadFile = {
  id: null,
  progress: 0,
  status: null,
  file: null,
  mimetype: null,
  length: null,
  name: null,
  preview: null,
  completed: false,
  rawFile: null,
  rawResponse: null,
}

type FileAction =
  | { type: 'ADD_FILES'; files: UploadFile[]; replace: boolean }
  | { type: 'UPDATE_FILE'; fileId: string; data: Partial<UploadFile> }
  | { type: 'DELETE_FILE'; fileId: string }
  | { type: 'RESET' }

function filesReducer(state: UploadFile[], action: FileAction): UploadFile[] {
  switch (action.type) {
    case 'ADD_FILES':
      return action.replace ? action.files : [...state, ...action.files]
    case 'UPDATE_FILE':
      return state.map((file) =>
        file.id === action.fileId ? { ...file, ...action.data } : file
      )
    case 'DELETE_FILE':
      return state.filter((file) => file.id !== action.fileId)
    case 'RESET':
      return []
    default:
      return state
  }
}

// Helper functions
const formatBatchItemToUploadFile = (item: BatchItem): UploadFile => ({
  ...defaultUploadFile,
  id: item.id,
  mimetype: item.file.type,
  length: item.file.size,
  name: item.file.name,
  rawFile: item.file,
  rawResponse: item?.uploadResponse,
})

const formatInitialFilesToUploadFile = (
  files: InitialUploadFile[]
): UploadFile[] =>
  files.map((initialFile) => ({
    ...defaultUploadFile,
    ...initialFile,
    status: UploadFileStatuses.SUCCESS,
    completed: true,
  }))

// Main component
export const UploadComponent: FC<IUploadProps> = (props) => {
  const uploady = useUploady()
  const inputRef = useRef<HTMLInputElement>(null)

  // Default props
  const {
    id,
    analyticsIds = {},
    initialFiles = [],
    multiple = true,
    readonly = false,
    disabled = false,
    uploadDisabled = false,
    replaceFilelist = false,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    strings: stringsProp = {},
    components: componentsProp = {},
    onDelete = () => undefined,
    getPreviewFile = getImagePreview,
    getFileUrl = (res) => res?.data?.file || null,
    getExternalId = (res) => res?.data?.id || null,
    onFileAdd = () => undefined,
    onChange = () => null,
    onBatchAdd = () => null,
    onBatchComplete = () => null,
    onItemError = () => null,
    allowedFileTypes = [],
  } = props

  const strings = { ...defaultStrings, ...stringsProp }

  const [isUploading, setIsUploading] = useState(false)

  // State management with reducer
  const [files, dispatch] = useReducer(
    filesReducer,
    formatInitialFilesToUploadFile(initialFiles)
  )

  // Connect file input to Uploady
  useFileInput(inputRef)

  // Track batch progress
  const batchProgress = useBatchProgressListener()

  // File management functions
  const updateFile = useCallback(
    (fileId: string, data: Partial<UploadFile>) => {
      dispatch({ type: 'UPDATE_FILE', fileId, data })

      // Call onChange with the updated file
      const updatedFile = files.find((f) => f.id === fileId)
      if (updatedFile) {
        onChange([{ ...updatedFile, ...data }], files)
      }
    },
    [files, onChange]
  )

  const addFiles = useCallback(
    (newFiles: UploadFile[], replace: boolean) => {
      dispatch({ type: 'ADD_FILES', files: newFiles, replace })
      onChange(newFiles, replace ? [] : files)
    },
    [files, onChange]
  )

  const deleteFile = useCallback(
    async (fileOrFileID: string | UploadFile) => {
      const fileId =
        typeof fileOrFileID === 'string' ? fileOrFileID : fileOrFileID?.id

      const fileToDelete = files.find((file) => file.id === fileId)

      if (!fileToDelete) return

      updateFile(fileId, { status: UploadFileStatuses.DELETING })

      try {
        const result = await Promise.resolve(onDelete(fileToDelete))

        if (result === false) {
          updateFile(fileId, { status: UploadFileStatuses.SUCCESS })
        } else {
          dispatch({ type: 'DELETE_FILE', fileId })
        }
      } catch (error) {
        updateFile(fileId, { status: UploadFileStatuses.SUCCESS })
      }
    },
    [files, onDelete, updateFile]
  )

  const cancelFile = useCallback(
    (fileOrFileID: string | UploadFile) => {
      const fileId =
        typeof fileOrFileID === 'string' ? fileOrFileID : fileOrFileID?.id

      const fileToCancel = files.find((file) => file.id === fileId)
      if (!fileToCancel) return

      uploady.abort(fileId)
      dispatch({ type: 'DELETE_FILE', fileId })
    },
    [files, uploady]
  )

  const resetFiles = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  // Preview generation
  const generatePreview = useCallback(
    async (file: UploadFile) => {
      if (!getPreviewFile || !file.rawFile) return

      try {
        const previewData = await Promise.resolve(
          getPreviewFile(file.rawFile as File)
        )
        updateFile(file.id, { preview: previewData })
      } catch (error) {
        console.error('Failed to generate preview:', error)
      }
    },
    [getPreviewFile, updateFile]
  )

  useBatchAddListener(async (batch: Batch) => {
    const newUploadFiles = batch.items.map((batchItem) => ({
      ...formatBatchItemToUploadFile(batchItem),
      status: UploadFileStatuses.ADDED,
    }))

    try {
      const batchAddResult = await Promise.resolve(
        onBatchAdd(newUploadFiles, files)
      )

      if (batchAddResult === false) {
        uploady.abortBatch(batch.id)
        return
      }

      const validationResults = await Promise.all(
        newUploadFiles.map((file) => Promise.resolve(onFileAdd(file)))
      )

      const validFiles = newUploadFiles.filter(
        (_, index) => validationResults[index] !== false
      )

      addFiles(validFiles, replaceFilelist)

      validFiles.forEach(generatePreview)
    } catch (error) {
      console.error('Error processing batch:', error)
      uploady.abortBatch(batch.id)
    }
  })

  useItemStartListener((batchItem: BatchItem) => {
    setIsUploading(true)

    // File type validation
    if (
      allowedFileTypes?.length > 0 &&
      !allowedFileTypes.includes(batchItem.file.type)
    ) {
      updateFile(batchItem.id, {
        status: UploadFileStatuses.ERROR,
        error: strings.unacceptedFileType,
      })
      return false
    }

    // File size validation
    if (maxFileSize && batchItem.file.size > maxFileSize) {
      updateFile(batchItem.id, {
        status: UploadFileStatuses.ERROR,
        error: strings.exceedsSizeLimit,
      })
      return false
    }

    return true
  })

  useItemErrorListener((batchItem: BatchItem) => {
    const erroredFile = files.find((f) => f.id === batchItem.id)

    updateFile(batchItem.id, {
      status: UploadFileStatuses.ERROR,
      rawResponse: batchItem.uploadResponse,
    })

    if (erroredFile) {
      onItemError({
        ...erroredFile,
        status: UploadFileStatuses.ERROR,
        rawResponse: batchItem.uploadResponse,
      })
    }
  })

  useItemProgressListener((batchItem: BatchItem) => {
    const progress = Math.floor((batchItem.loaded / batchItem.file.size) * 100)
    updateFile(batchItem.id, {
      progress,
      status: UploadFileStatuses.PROGRESS,
    })
  })

  const getCompletedBatchItemState = useCallback(
    (batchItem: BatchItem): Partial<UploadFile> => ({
      progress: 100,
      status: UploadFileStatuses.SUCCESS,
      completed: true,
      file: getFileUrl(batchItem.uploadResponse),
      externalId: getExternalId(batchItem.uploadResponse),
      rawResponse: batchItem.uploadResponse,
    }),
    [getFileUrl, getExternalId]
  )

  useItemFinishListener((batchItem: BatchItem) => {
    updateFile(batchItem.id, getCompletedBatchItemState(batchItem))
  })

  useBatchFinishListener((batch: Batch) => {
    const updatedFiles = files.map((file) => {
      const batchItem = batch.items.find((item) => file.id === item.id)
      if (!batchItem) return file
      if (batchItem.state === FILE_STATES.ERROR) {
        return {
          ...file,
          status: UploadFileStatuses.ERROR,
          rawResponse: batchItem.uploadResponse,
        }
      }
      return { ...file, ...getCompletedBatchItemState(batchItem) }
    })

    const completedBatchFiles = updatedFiles.filter((file) =>
      batch.items.some((item) => file.id === item.id)
    )

    onBatchComplete(completedBatchFiles, updatedFiles)

    setIsUploading(
      updatedFiles.some(
        (file) => !file.completed && file.status === UploadFileStatuses.PROGRESS
      )
    )
  })

  // Interaction handlers
  const showFileUpload = useCallback(() => {
    uploady?.showFileUpload()
  }, [uploady])

  const uploadFile = useCallback(
    (filesToUpload: UploadInfo | UploadInfo[]) => {
      uploady.upload(filesToUpload)
    },
    [uploady]
  )

  // Create common props object
  const commonProps: UploadCommonProps = useMemo(() => {
    const progress = batchProgress?.completed || 0

    return {
      files,
      batchProgress: progress,
      isMultiple: multiple,
      isReadonly: readonly,
      isDisabled: disabled,
      isUploading,
      isUploadDisabled: uploadDisabled,
      acceptFiles: allowedFileTypes,
      showFileUpload,
      uploadFile,
      deleteFile,
      cancelFile,
      reset: resetFiles,
      uploadProps: {
        ...props,
        analyticsIds,
        maxFileSize,
        strings,
      },
      strings,
    }
  }, [
    files,
    batchProgress,
    multiple,
    readonly,
    disabled,
    uploadDisabled,
    allowedFileTypes,
    showFileUpload,
    uploadFile,
    deleteFile,
    cancelFile,
    resetFiles,
    props,
    analyticsIds,
    maxFileSize,
    strings,
    isUploading,
  ])

  // Memoize context value
  const contextValue = useMemo(() => ({ commonProps }), [commonProps])

  // Memoize components
  const Components = useMemo<UploadComponents>(
    () => ({
      ...UploadComponents,
      ...componentsProp,
    }),
    [componentsProp]
  )

  const renderFileActionContainer = (file: UploadFile) => {
    const showCancelButton =
      !commonProps.isReadonly &&
      !commonProps.isDisabled &&
      !file.completed &&
      file.status === UploadFileStatuses.PROGRESS

    const showDeleteButton =
      !commonProps.isReadonly &&
      !commonProps.isDisabled &&
      (file.completed || file.status === UploadFileStatuses.ERROR)

    return (
      <Components.FileActionContainer
        commonProps={commonProps}
        innerProps={{
          file,
          cancelButton: showCancelButton ? (
            <WithTooltip title='Cancel upload' placement='top'>
              <span>
                <TransparentIconButton
                  aria-label='cancel'
                  icon='times'
                  onClick={() => cancelFile(file)}
                />
              </span>
            </WithTooltip>
          ) : null,
          deleteButton: showDeleteButton ? (
            <WithTooltip title='Delete file' placement='top'>
              <span>
                <TransparentIconButton
                  type='button'
                  aria-label={`Delete ${file.name || file.file}`}
                  icon='delete'
                  onClick={() => deleteFile(file)}
                  isLoading={file.status === UploadFileStatuses.DELETING}
                  disabled={file.status === UploadFileStatuses.DELETING}
                />
              </span>
            </WithTooltip>
          ) : null,
        }}
      />
    )
  }

  const renderProgressIndicator = (file: UploadFile) => {
    if (
      commonProps.isReadonly ||
      commonProps.isDisabled ||
      file.completed ||
      file.status !== UploadFileStatuses.PROGRESS
    ) {
      return null
    }

    return (
      <Components.UploadProgress
        commonProps={commonProps}
        innerProps={{ progress: file.progress }}
      />
    )
  }

  return (
    <UploadContext.Provider value={contextValue}>
      <Components.UploadDragDropWrapper
        disabled={disabled || readonly || uploadDisabled}
        onDrop={(droppedFiles) => uploady.upload(droppedFiles as any)}
        commonProps={commonProps}
        innerProps={{ maxFileSize: filesize(maxFileSize) }}
      >
        <UploaderStyled
          id={id}
          data-status={isUploading ? 'uploading' : 'idle'}
        >
          <Components.MainContainer commonProps={commonProps}>
            <Components.UploadContainer commonProps={commonProps}>
              <Components.UploadButton commonProps={commonProps} />
              <Components.UploadText
                commonProps={commonProps}
                innerProps={{ maxFileSize: filesize(maxFileSize) }}
              >
                <SubText>{strings.uploadInfo}</SubText>
              </Components.UploadText>
            </Components.UploadContainer>

            <Components.FileListContainer commonProps={commonProps}>
              {files.length > 0 ? (
                <Components.FileList commonProps={commonProps}>
                  {files.map((file) => (
                    <Components.UploadFileContainer
                      key={file.id}
                      commonProps={commonProps}
                      aria-label={file.name}
                      data-status={file.status}
                      innerProps={{ file }}
                    >
                      <Space
                        direction='horizontal'
                        size='small'
                        justify='space-between'
                      >
                        <Components.UploadFile
                          commonProps={commonProps}
                          innerProps={{ file }}
                        />
                        {renderProgressIndicator(file)}
                        {renderFileActionContainer(file)}
                      </Space>
                    </Components.UploadFileContainer>
                  ))}
                </Components.FileList>
              ) : (
                <Components.UploadEmptyState
                  commonProps={commonProps}
                  innerProps={{ maxFileSize: filesize(maxFileSize) }}
                />
              )}
            </Components.FileListContainer>
          </Components.MainContainer>

          <input
            type='file'
            ref={inputRef}
            id={`uploader-fileinput${id ? `-${id}` : ''}`}
            multiple={multiple}
            accept={allowedFileTypes.join(', ')}
            aria-label='File upload'
            aria-hidden='true'
            tabIndex={-1}
          />
        </UploaderStyled>
      </Components.UploadDragDropWrapper>
    </UploadContext.Provider>
  )
}

export const Upload: FC<IUploadProps> = (props) => (
  <BaseUpload {...props}>
    <UploadComponent {...props} />
  </BaseUpload>
)

Upload.displayName = 'Upload'
